import {useEffect, useCallback, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

declare global {
  interface Window {
    Cookiebot?: {
      consent?: {statistics: boolean; marketing: boolean}
      consentUTC?: string
      consentID?: string
      hasResponse?: boolean
      show: () => void
      submitCustomConsent: (preferences: boolean, statistics: boolean, marketing: boolean) => void
      withdraw: () => void
    }
    openCookieSettings?: () => void
    CookiebotCallback_OnConsentReady?: () => void
    showCookieBannerDetails?: () => void
  }
}

/**
 * Global Cookiebot cookie-settings popup and trigger button.
 * Renders the floating cookie icon button and the settings popup dialog.
 * Must be mounted once at the app root level.
 */
const CookieSettings = () => {
  const intl = useIntl()
  const popupJustOpenedRef = useRef(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  // Ref mirror of isPopupOpen — lets the outside-click handler read current
  // popup state without being a useEffect dependency, preventing the effect
  // from re-registering globals and the click listener on every open/close.
  const isPopupOpenRef = useRef(false)
  const bannerFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const detailsPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const localeRef = useRef(intl.locale)

  // Keep locale ref in sync so CookiebotCallback_OnConsentReady always uses current locale
  useEffect(() => {
    localeRef.current = intl.locale
  }, [intl.locale])

  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [isAllAccepted, setIsAllAccepted] = useState(false)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [consentDate, setConsentDate] = useState<string | null>(null)
  const [consentId, setConsentId] = useState<string | null>(null)

  // Keep the ref in sync with state
  useEffect(() => {
    isPopupOpenRef.current = isPopupOpen
  }, [isPopupOpen])

  // Clear timers if the component unmounts mid-animation or mid-poll
  useEffect(() => {
    return () => {
      if (bannerFadeTimerRef.current !== null) clearTimeout(bannerFadeTimerRef.current)
      if (detailsPollRef.current !== null) clearInterval(detailsPollRef.current)
    }
  }, [])

  const syncConsentState = useCallback((locale: string) => {
    if (!window.Cookiebot) return
    const allOn = !!window.Cookiebot.consent?.statistics && !!window.Cookiebot.consent?.marketing
    setIsAllAccepted(allOn)
    setConsentDate(
      window.Cookiebot.consentUTC
        ? new Date(window.Cookiebot.consentUTC).toLocaleString(locale, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short',
          })
        : null
    )
    setConsentId(window.Cookiebot.consentID ?? null)
  }, [])

  const handlePopupOpen = useCallback(() => {
    syncConsentState(intl.locale)
    setIsPopupOpen(true)
    popupJustOpenedRef.current = true
  }, [syncConsentState, intl.locale])

  const handlePopupClose = useCallback(() => {
    setIsPopupOpen(false)
  }, [])

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      setIsAllAccepted(checked)
      if (checked) {
        window.Cookiebot?.submitCustomConsent(true, true, true)
      } else {
        window.Cookiebot?.withdraw()
        handlePopupClose()
      }
    },
    [handlePopupClose]
  )

  const handleOpenBanner = useCallback(
    (openDetails = false) => {
      const showBanner = () => {
        if (openDetails) {
          if (typeof window.showCookieBannerDetails === 'function') {
            // Template script already loaded — go straight to details
            window.showCookieBannerDetails()
          } else {
            // Template script not loaded yet — call show() to load it,
            // then open details once the DOM is ready
            document.documentElement.classList.add('cb-details-pending')
            window.Cookiebot?.show()
            let attempts = 0
            const maxAttempts = 60 // 60 × 50ms = 3s max
            detailsPollRef.current = setInterval(() => {
              attempts++
              const notice = document.getElementById('CookieBannerNotice')
              const details = document.getElementById('CookieBannerDetails')
              const fnReady = typeof window.showCookieBannerDetails === 'function'
              if ((notice && details && fnReady) || attempts >= maxAttempts) {
                clearInterval(detailsPollRef.current!)
                detailsPollRef.current = null
                document.documentElement.classList.remove('cb-details-pending')
                if (notice && details && fnReady) {
                  // Hide what show() rendered, then open details cleanly
                  notice.style.display = 'none'
                  window.showCookieBannerDetails!()
                }
              }
            }, 50)
          }
        } else {
          window.Cookiebot?.show()
        }
      }

      if (isPopupOpenRef.current) {
        // Fade out the popup, then show the banner
        const popup = popupRef.current
        if (popup) {
          popup.style.opacity = '0'
          bannerFadeTimerRef.current = setTimeout(() => {
            popup.style.opacity = ''
            setIsPopupOpen(false)
            showBanner()
          }, 150)
        } else {
          setIsPopupOpen(false)
          showBanner()
        }
      } else {
        handlePopupClose()
        showBanner()
      }
    },
    [handlePopupClose]
  )

  const handleOpenBannerSettings = useCallback(() => handleOpenBanner(true), [handleOpenBanner])

  const handleChangeConsent = useCallback(() => handleOpenBanner(), [handleOpenBanner])

  const handleToggleDetails = useCallback(() => {
    setIsDetailsOpen((prev) => !prev)
  }, [])

  const handleOpenCookieSettings = useCallback(() => {
    if (window.Cookiebot?.hasResponse) {
      handlePopupOpen()
    } else {
      window.Cookiebot?.show()
    }
  }, [handlePopupOpen])

  // Register global functions and event listeners.
  // Stable deps only — isPopupOpen is read via isPopupOpenRef to avoid
  // re-registering on every popup open/close.
  useEffect(() => {
    // Required: Cookiebot consent banner template calls window.openCookieSettings
    // to re-open the popup after the user dismisses the banner.
    window.openCookieSettings = handleOpenCookieSettings

    window.CookiebotCallback_OnConsentReady = () => {
      document.body.style.overflow = ''
      syncConsentState(localeRef.current)
    }

    const handleOutsideClick = (e: MouseEvent) => {
      if (popupJustOpenedRef.current) {
        popupJustOpenedRef.current = false
        return
      }
      if (!popupRef.current || !isPopupOpenRef.current) return
      if (!popupRef.current.contains(e.target as Node) && e.target !== triggerRef.current) {
        handlePopupClose()
      }
    }

    document.addEventListener('click', handleOutsideClick)

    return () => {
      document.removeEventListener('click', handleOutsideClick)
      delete window.openCookieSettings
      delete window.CookiebotCallback_OnConsentReady
    }
  }, [handleOpenCookieSettings, handlePopupClose, syncConsentState])

  const triggerButton = createPortal(
    <button
      ref={triggerRef}
      id='CookiebotCustomTrigger'
      aria-label={intl.formatMessage({id: 'COOKIE_SETTINGS_OPEN'})}
      onClick={handleOpenCookieSettings}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='22'
        height='22'
        viewBox='0 0 22 22'
        fill='none'
        aria-hidden='true'
        style={{pointerEvents: 'none'}}
      >
        <path
          d='M10.9375 21.9023C9.42448 21.9023 8.0026 21.6152 6.67188 21.041C5.34115 20.4668 4.18359 19.6875 3.19922 18.7031C2.21484 17.7188 1.43555 16.5612 0.861328 15.2305C0.287109 13.8997 0 12.4779 0 10.9648C0 9.59766 0.264323 8.25781 0.792969 6.94531C1.32161 5.63281 2.0599 4.46159 3.00781 3.43164C3.95573 2.40169 5.09505 1.57227 6.42578 0.943359C7.75651 0.314453 9.21484 0 10.8008 0C11.1836 0 11.5755 0.0182292 11.9766 0.0546875C12.3776 0.0911458 12.7878 0.154948 13.207 0.246094C13.043 1.06641 13.0977 1.84115 13.3711 2.57031C13.6445 3.29948 14.0547 3.9056 14.6016 4.38867C15.1484 4.87174 15.8001 5.20443 16.5566 5.38672C17.3132 5.56901 18.0924 5.52344 18.8945 5.25C18.4206 6.32552 18.4889 7.35547 19.0996 8.33984C19.7103 9.32422 20.6172 9.83464 21.8203 9.87109C21.8385 10.0716 21.8522 10.2585 21.8613 10.4316C21.8704 10.6048 21.875 10.7917 21.875 10.9922C21.875 12.487 21.5879 13.8952 21.0137 15.2168C20.4395 16.5384 19.6602 17.696 18.6758 18.6895C17.6914 19.6829 16.5339 20.4668 15.2031 21.041C13.8724 21.6152 12.4505 21.9023 10.9375 21.9023ZM9.29688 8.77734C9.7526 8.77734 10.14 8.61784 10.459 8.29883C10.778 7.97982 10.9375 7.59245 10.9375 7.13672C10.9375 6.68099 10.778 6.29362 10.459 5.97461C10.14 5.6556 9.7526 5.49609 9.29688 5.49609C8.84115 5.49609 8.45378 5.6556 8.13477 5.97461C7.81576 6.29362 7.65625 6.68099 7.65625 7.13672C7.65625 7.59245 7.81576 7.97982 8.13477 8.29883C8.45378 8.61784 8.84115 8.77734 9.29688 8.77734ZM7.10938 14.2461C7.5651 14.2461 7.95247 14.0866 8.27148 13.7676C8.5905 13.4486 8.75 13.0612 8.75 12.6055C8.75 12.1497 8.5905 11.7624 8.27148 11.4434C7.95247 11.1243 7.5651 10.9648 7.10938 10.9648C6.65365 10.9648 6.26628 11.1243 5.94727 11.4434C5.62826 11.7624 5.46875 12.1497 5.46875 12.6055C5.46875 13.0612 5.62826 13.4486 5.94727 13.7676C6.26628 14.0866 6.65365 14.2461 7.10938 14.2461ZM14.2188 15.3398C14.5286 15.3398 14.7884 15.235 14.998 15.0254C15.2077 14.8158 15.3125 14.556 15.3125 14.2461C15.3125 13.9362 15.2077 13.6764 14.998 13.4668C14.7884 13.2572 14.5286 13.1523 14.2188 13.1523C13.9089 13.1523 13.6491 13.2572 13.4395 13.4668C13.2298 13.6764 13.125 13.9362 13.125 14.2461C13.125 14.556 13.2298 14.8158 13.4395 15.0254C13.6491 15.235 13.9089 15.3398 14.2188 15.3398Z'
          fill='white'
        />
      </svg>
    </button>,
    document.body
  )

  return (
    <>
      {triggerButton}
      <div
        ref={popupRef}
        id='CookieSettingsPopup'
        role='dialog'
        aria-modal='true'
        aria-labelledby='CookieSettingsTitle'
        style={{display: isPopupOpen ? 'block' : 'none'}}
      >
        <div className='csp__card'>
          <div className='csp__header'>
            <h2 id='CookieSettingsTitle' className='csp__title'>
              {intl.formatMessage({id: 'COOKIE_SETTINGS'})}
            </h2>
            <button
              className='csp__close'
              onClick={handlePopupClose}
              aria-label={intl.formatMessage({id: 'COOKIE_SETTINGS_CLOSE'})}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
              >
                <line x1='18' y1='6' x2='6' y2='18' />
                <line x1='6' y1='6' x2='18' y2='18' />
              </svg>
            </button>
          </div>
          <div className='csp__body'>
            <div className='csp__toggle-row'>
              <span className='csp__toggle-label'>
                {intl.formatMessage({id: 'COOKIE_SETTINGS_ACCEPT_ALL'})}
              </span>
              <label className='csp__toggle'>
                <input
                  type='checkbox'
                  role='switch'
                  aria-checked={isAllAccepted}
                  checked={isAllAccepted}
                  onChange={(e) => handleToggleAll(e.target.checked)}
                />
                <span className='csp__toggle-track'></span>
              </label>
            </div>
            <button className='csp__customize-link' onClick={handleOpenBannerSettings}>
              {intl.formatMessage({id: 'COOKIE_SETTINGS_CUSTOMIZE'})}
            </button>
            <div className='csp__details-section'>
              <button
                className='csp__show-details-btn'
                aria-expanded={isDetailsOpen}
                onClick={handleToggleDetails}
              >
                <span className='csp__show-details-label'>
                  {isDetailsOpen
                    ? intl.formatMessage({id: 'COOKIE_SETTINGS_HIDE_DETAILS'})
                    : intl.formatMessage({id: 'COOKIE_SETTINGS_SHOW_DETAILS'})}
                </span>
                <svg
                  className='csp__chevron'
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <polyline points='18 15 12 9 6 15' />
                </svg>
              </button>
              <div className={`csp__details-box${isDetailsOpen ? ' is-open' : ''}`}>
                <div className='csp__detail-row'>
                  <span className='csp__detail-key'>
                    {intl.formatMessage({id: 'COOKIE_SETTINGS_CONSENT_DATE'})}
                  </span>
                  <span className='csp__detail-val'>{consentDate ?? '—'}</span>
                </div>
                <div className='csp__detail-row'>
                  <span className='csp__detail-key'>
                    {intl.formatMessage({id: 'COOKIE_SETTINGS_CONSENT_ID'})}
                  </span>
                  <span className='csp__detail-val'>{consentId ?? '—'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className='csp__footer'>
            <button
              className='csp__btn-change'
              disabled={isAllAccepted}
              onClick={handleChangeConsent}
            >
              {intl.formatMessage({id: 'COOKIE_SETTINGS_CHANGE_CONSENT'})}
            </button>
            <button className='csp__btn-customize-mobile' onClick={handleOpenBannerSettings}>
              {intl.formatMessage({id: 'COOKIE_SETTINGS_CUSTOMIZE'})}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export {CookieSettings}
